/**
 * WS-01 내 입시카드(`/analysis/card`) 선택지·라벨 SSoT.
 *
 * 백엔드는 값(영문 코드·문자열)만 알고 **한글 라벨은 프론트가 소유**한다 — 라벨 문구를 바꿔도
 * DB 마이그레이션이 필요 없게 하기 위한 경계다(spec research R8). 따라서 화면에서 라벨을
 * 하드코딩하지 않고 반드시 이 파일의 배열·맵을 참조한다.
 *
 * ⚠️ 값(value)은 백엔드 enum 과 1:1 계약이다. 추가·변경 시 다음이 함께 바뀌어야 한다:
 *   - desiredTrack / highschoolType → Java enum DesiredTrack·HighschoolType (+ member_profile 컬럼 코멘트)
 *   - gradeSystem / grade           → Java enum GradeSystem + 서비스 등급 범위 검증
 *   - semester                      → 서비스 semester 허용값 검증
 *
 * 도메인 데이터라 config/(도메인 무지 인프라)가 아닌 data/analysis/ 에 둔다.
 */

/** value-label 쌍 — BaseSelect `options` 계약과 동일 형태. */
export interface CardOption {
  value: string;
  label: string;
}

// ── 섹션 1: 기본 정보 ────────────────────────────────────────────────

/**
 * 학년 — 값은 **영문 코드**(H1·H2·H3·N)다. V949 가 기존 한글 자유값(`'고3'`)을 코드로 정규화했다.
 *
 * 코드로 통일한 이유: 가입 폼 세그먼트(`member_profile.student_grade`)와 같은 개념인데 한쪽만
 * 한글이면 가입→입시카드 프리필마다 `H3`↔`'고3'` 변환이 붙는다.
 *
 * 🔴 **학년 선택지의 SSoT 는 여기 하나다.** 가입 폼은 이 상수를 `STUDENT_GRADE_OPTIONS` 라는
 * 이름으로 재수출해 쓴다(`signupSegmentOptions.ts`) — 두 벌로 두면 한쪽만 바뀔 때 가입→입시카드
 * 프리필이 조용히 깨지므로 정의를 합쳤다(2026-08-13). 값을 고치면 두 화면이 함께 바뀐다.
 *
 * ⚠️ value 를 한글로 되돌리면 DB 에는 코드가, 화면에서는 한글이 오가 저장이 깨진다.
 */
export const GRADE_OPTIONS: CardOption[] = [
  { value: 'H1', label: '고1' },
  { value: 'H2', label: '고2' },
  { value: 'H3', label: '고3' },
  { value: 'N', label: 'N수' },
];

/** 희망 계열 — 화면 필수. 계열에 따라 비교 모집단위가 갈린다. */
export const DESIRED_TRACK_OPTIONS: CardOption[] = [
  { value: 'HUMANITIES', label: '인문' },
  { value: 'SCIENCE', label: '자연' },
];

/** 고교 유형 — 선택 입력(미선택 저장 허용). */
export const HIGHSCHOOL_TYPE_OPTIONS: CardOption[] = [
  { value: 'GENERAL', label: '일반고' },
  { value: 'AUTONOMOUS', label: '자사고' },
  { value: 'FOREIGN_INTL', label: '외고·국제고' },
  { value: 'SCIENCE_GIFTED', label: '과학고·영재고' },
  { value: 'SPECIALIZED', label: '특성화고' },
  { value: 'GED', label: '검정고시' },
];

// ── 섹션 3: 내신 성적 ────────────────────────────────────────────────

/** 학년-학기 6개 값 — 백엔드 semester 허용값과 동일 집합. */
export const TERM_OPTIONS: CardOption[] = [
  { value: '1-1', label: '1-1' },
  { value: '1-2', label: '1-2' },
  { value: '2-1', label: '2-1' },
  { value: '2-2', label: '2-2' },
  { value: '3-1', label: '3-1' },
  { value: '3-2', label: '3-2' },
];

/** 등급 체계 — 성적표 표기를 학생이 고른다(학생당 1개, 행별 혼합 없음). */
export type GradeSystem = 'NINE' | 'FIVE';

/** 등급 체계별 최대 등급 — 등급 선택지 범위·저장 검증의 근거값. */
export const GRADE_SYSTEM_MAX: Record<GradeSystem, number> = {
  NINE: 9,
  FIVE: 5,
};

export const GRADE_SYSTEM_OPTIONS: { value: GradeSystem; label: string }[] = [
  { value: 'NINE', label: '9등급제' },
  { value: 'FIVE', label: '5등급제' },
];

/** 등급 체계에 맞는 등급 선택지(1~9 또는 1~5)를 만든다. */
export function gradeOptions(system: GradeSystem): CardOption[] {
  return Array.from({ length: GRADE_SYSTEM_MAX[system] }, (_, i) => {
    const g = String(i + 1);
    return { value: g, label: `${g}등급` };
  });
}

// ── 섹션 2: 수능 성적 ────────────────────────────────────────────────

/** 수능 참고 항목(영어·한국사) 등급 선택지 — 항상 9등급제. */
export const SUNEUNG_GRADE_OPTIONS: CardOption[] = gradeOptions('NINE');

// ── 라벨 조회 헬퍼 ──────────────────────────────────────────────────

/** 값 → 한글 라벨. 미지의 값(구 데이터 등)은 값을 그대로 노출해 빈칸이 되지 않게 한다. */
export function labelOf(options: CardOption[], value: string | null | undefined): string {
  if (!value) return '';
  return options.find((o) => o.value === value)?.label ?? value;
}
